import { Button } from "@components/ui/Button"
import { cn } from "@lib/cn"
import { formatFileSize } from "@lib/intl"
import { Camera, Upload, X } from "lucide-react"
import { useRef, useState, type DragEvent } from "react"
import { useTranslation } from "react-i18next"

import {
  REFERENCE_FILE_PICKER_ACCEPT,
  REFERENCE_IMAGE_PICKER_ACCEPT,
  REFERENCE_MAX_FILE_BYTES,
  REFERENCE_MAX_IMAGE_BYTES,
} from "@/constants/references"

interface Props {
  kind: "file" | "image"
  file: File | null
  onPick: (file: File) => void
  onClear: () => void
  disabled?: boolean
}

const MB = 1024 * 1024

/**
 * One file in, drag-and-drop or picker, with a camera button for images on
 * a phone. The same drag-depth guard as the student portal: a drag that
 * carries no files never lights the zone. Validation lives in the modal,
 * where the blocked reason is shown next to the submit button.
 */
export function ReferenceDropzone({ kind, file, onPick, onClear, disabled }: Props) {
  const { t } = useTranslation("references")
  const inputRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  const carriesFiles = (dt: DataTransfer | null) => !!dt?.types.includes("Files")

  const accept = kind === "image" ? REFERENCE_IMAGE_PICKER_ACCEPT : REFERENCE_FILE_PICKER_ACCEPT
  const maxMb = (kind === "image" ? REFERENCE_MAX_IMAGE_BYTES : REFERENCE_MAX_FILE_BYTES) / MB

  function pickFrom(list: FileList | null) {
    const picked = list?.[0]
    if (picked) onPick(picked)
  }

  function onDragEnter(e: DragEvent<HTMLDivElement>) {
    if (disabled || !carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    depth.current += 1
    setDragging(true)
  }
  function onDragOver(e: DragEvent<HTMLDivElement>) {
    if (disabled || !carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
  }
  function onDragLeave() {
    depth.current = Math.max(0, depth.current - 1)
    if (depth.current === 0) setDragging(false)
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    if (disabled || !carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    depth.current = 0
    setDragging(false)
    pickFrom(e.dataTransfer.files)
  }

  if (file) {
    return (
      <div className="rounded-control border-border bg-inset flex items-center gap-3 border px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-ink truncate text-sm font-medium">{file.name}</p>
          <p className="text-meta">{formatFileSize(file.size)}</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          aria-label={t("add.dropzone.remove", { name: file.name })}
          className="text-ink-muted hover:text-ink rounded-control flex h-9 w-9 items-center justify-center md:h-8 md:w-8"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    )
  }

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "rounded-card flex flex-col items-center justify-center gap-2 border-2 border-dashed px-4 py-6 text-center transition-colors",
        dragging
          ? "border-accent bg-accent-soft text-accent-ink"
          : "border-border bg-inset text-ink-muted",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          pickFrom(e.target.files)
          e.target.value = ""
        }}
      />
      {kind === "image" ? (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          disabled={disabled}
          onChange={(e) => {
            pickFrom(e.target.files)
            e.target.value = ""
          }}
        />
      ) : null}
      <Upload className="h-6 w-6" aria-hidden />
      <p className="text-ink text-sm font-medium">
        {kind === "image" ? t("add.dropzone.image") : t("add.dropzone.file")}
      </p>
      <p className="text-meta">
        {kind === "image"
          ? t("add.dropzone.constraints_image", { max: maxMb })
          : t("add.dropzone.constraints_file", { max: maxMb })}
      </p>
      <div className="mt-1 flex flex-wrap justify-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          {t("add.dropzone.pick")}
        </Button>
        {kind === "image" ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="md:hidden"
            leftIcon={<Camera className="h-4 w-4" />}
            onClick={() => cameraRef.current?.click()}
            disabled={disabled}
          >
            {t("add.dropzone.take_photo")}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
